import { createSelector } from 'reselect';
import { get, find } from 'lodash';
import {
  selectQuestionnaireActiveQuestionId,
  makeSelectActiveQuestion,
} from 'containers/QuestionnairePage/selectors';
import { DISQUALIFY_PLACEHOLDER_QUESTION } from 'containers/QuestionnairePage/constants';
import {
  makeSelectSurveyType,
  makeSelectSurveyStatusName,
} from 'containers/Survey/selectors';
import { surveyStatusEnum } from 'containers/SurveysPage/constants';

import {
  OutcomeType,
  OutcomeGraphTypes,
  questionnaireLogicService,
  OutcomeActionType,
  QuotaConditionTypeEnum,
} from 'utils/adl-services';

import { SURVEY_TYPES_EDIT_QUESTIONNAIRE_LOGIC } from './constants';
import {
  filterStatementsByNodeIdByOutcomeTypes,
  getNonQuotaStatements,
  getFilteredQuotaStatementsByType,
  filterVisibleNodeStatements,
} from './utils';

/**
 * Direct selector to the questionnaireLogic state domain
 */
const selectQuestionnaireLogicDomain = (state) =>
  get(state, 'questionnairePage.questionnaireLogic', {});

const selectDisqualificationLogicDomain = (state) =>
  get(state, 'questionnairePage.disqualificationLogic', {});

const selectDisqualifiedUsersDomain = (state) =>
  get(state, 'questionnairePage.disqualifiedUsers', {});

/**
 * Other specific selectors
 */

/**
 * Default selector used by QuestionnaireLogic
 */

const makeSelectQuestionnaireLogic = createSelector(
  selectQuestionnaireLogicDomain,
  (substate) => substate,
);

const makeSelectQuestionnaireLogicToStore = createSelector(
  selectQuestionnaireLogicDomain,
  makeSelectSurveyType,
  makeSelectSurveyStatusName,
  (substate, surveyType, surveyStatusName) => {
    if (
      !SURVEY_TYPES_EDIT_QUESTIONNAIRE_LOGIC.includes(surveyType) ||
      surveyStatusName !== surveyStatusEnum.UNDER_EDIT
    ) {
      return {};
    }

    const logic = substate || {};
    const ql = get(logic, 'ql', {});
    const cleanedQl = questionnaireLogicService.cleanFromEmptyValues(ql);

    return { ...logic, ql: cleanedQl };
  },
);

const makeSelectDisqualificationLogicToStore = createSelector(
  selectDisqualificationLogicDomain,
  (substate) => {
    const logic = substate || {};
    const ql = get(logic, 'ql', {});
    const cleanedQl = questionnaireLogicService.cleanFromEmptyValues(ql);

    return { ...logic, ql: cleanedQl };
  },
);

const makeSelectDisqualifiedUsers = createSelector(
  selectDisqualifiedUsersDomain,
  (substate) => substate,
);

const makeSelectActiveNodeAllStatements = createSelector(
  selectQuestionnaireLogicDomain,
  makeSelectActiveQuestion,
  (substate, { isScreening, refId: activeNodeId } = {}) => {
    if (isScreening) {
      return [];
    }
    return filterStatementsByNodeIdByOutcomeTypes(
      substate.ql,
      activeNodeId,
      OutcomeGraphTypes,
    );
  },
);

const makeSelectActiveNodeStatements = createSelector(
  makeSelectActiveNodeAllStatements,
  makeSelectActiveQuestion,
  (allActiveNodeStatements, activeQuestion) =>
    filterVisibleNodeStatements(
      getNonQuotaStatements(allActiveNodeStatements),
      activeQuestion,
    ),
);

const makeSelectActiveNodeAdvancedQuotaStatements = createSelector(
  makeSelectActiveNodeAllStatements,
  (allActiveNodeStatements) =>
    getFilteredQuotaStatementsByType(
      allActiveNodeStatements,
      QuotaConditionTypeEnum.Advanced,
    ),
);
const makeSelectActiveNodeSimpleQuotaStatements = createSelector(
  makeSelectActiveNodeAllStatements,
  (allActiveNodeStatements) =>
    getFilteredQuotaStatementsByType(
      allActiveNodeStatements,
      QuotaConditionTypeEnum.Simple,
    ),
);
const makeSelectDisqNodeStatements = createSelector(
  selectDisqualificationLogicDomain,
  (substate) => {
    const nodesStatements = get(substate, 'ql.nodeStatements', []);
    const nodeStatements = find(nodesStatements, [
      'nodeId',
      DISQUALIFY_PLACEHOLDER_QUESTION.id,
    ]);

    return get(nodeStatements, 'statements', undefined);
  },
);

// NOTE: Currently not used. Deprecate if it's still unused by Jan 2022
const makeSelectAllPaths = createSelector(
  selectQuestionnaireLogicDomain,
  (substate) => get(substate, 'allPaths', []),
);

const makeSelectPreviousNodesMap = createSelector(
  selectQuestionnaireLogicDomain,
  (substate) => get(substate, 'previousNodesPerNode', {}),
);

const makeSelectHiddenVariablesNodeStatements = createSelector(
  selectQuestionnaireLogicDomain,
  selectQuestionnaireActiveQuestionId,
  (substate, activeNodeId) =>
    filterStatementsByNodeIdByOutcomeTypes(substate.ql, activeNodeId, [
      OutcomeType.Variable,
    ]),
);

const makeSelectDisplayLogicNodeStatements = createSelector(
  selectQuestionnaireLogicDomain,
  selectQuestionnaireActiveQuestionId,
  (substate, activeNodeId) => {
    const filteredStatements = filterStatementsByNodeIdByOutcomeTypes(
      substate.ql,
      activeNodeId,
      [OutcomeType.Node, OutcomeType.Answer],
    );

    return filteredStatements.filter((statement) => {
      if (statement.outcome.type === OutcomeType.Answer) {
        return true;
      }

      // Hide node statement is not shown
      if (
        statement.outcome.type === OutcomeType.Node &&
        statement.outcome.actions[0]?.type === OutcomeActionType.Hide
      ) {
        return false;
      }

      return true;
    });
  },
);

export default makeSelectQuestionnaireLogic;
export {
  selectQuestionnaireLogicDomain,
  makeSelectActiveNodeStatements,
  makeSelectActiveNodeAdvancedQuotaStatements,
  makeSelectActiveNodeSimpleQuotaStatements,
  makeSelectDisqNodeStatements,
  makeSelectAllPaths,
  makeSelectPreviousNodesMap,
  makeSelectDisqualificationLogicToStore,
  makeSelectQuestionnaireLogicToStore,
  makeSelectDisqualifiedUsers,
  makeSelectHiddenVariablesNodeStatements,
  makeSelectDisplayLogicNodeStatements,
};
